/**
 * Extracted LaTeX Viewer Component
 * Displays the LaTeX text extracted from PDF
 */

import React, { useState, useEffect } from 'react';
import { Copy, Check, FileCode } from 'lucide-react';
import { useProcessingStore } from '../store/useProcessingStore';
import axios from 'axios';
import { joinUrl } from '../utils/url';

export const ExtractedTextViewer: React.FC = () => {
  const { processId, serverUrl } = useProcessingStore();
  const [extractedText, setExtractedText] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!processId) return;

    const fetchExtractedText = async () => {
      try {
        const response = await axios.get(joinUrl(serverUrl, `/api/status/${processId}`));
        if (response.data.extractedText) {
          setExtractedText(response.data.extractedText);
        }
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch extracted text:', error);
        setLoading(false);
      }
    };

    fetchExtractedText();

    // Poll for updates
    const interval = setInterval(fetchExtractedText, 3000);
    return () => clearInterval(interval);
  }, [processId, serverUrl]);

  const handleCopy = () => {
    navigator.clipboard.writeText(extractedText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8 text-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto"></div>
        <p className="mt-4 text-gray-600">正在加载提取的内容...</p>
      </div>
    );
  }

  if (!extractedText) {
    return (
      <div className="bg-white rounded-lg shadow-lg p-8 text-center">
        <FileCode className="w-16 h-16 text-gray-300 mx-auto mb-4" />
        <p className="text-gray-600">暂无提取的内容</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-teal-600 p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <FileCode className="w-6 h-6" />
            <div>
              <h3 className="text-xl font-bold">提取的 LaTeX/文本内容</h3>
              <p className="text-sm text-green-100 mt-1">
                从 PDF 中提取的原始内容
              </p>
            </div>
          </div>
          <button
            onClick={handleCopy}
            className="flex items-center space-x-2 px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-all"
          >
            {copied ? (
              <>
                <Check className="w-4 h-4" />
                <span className="text-sm">已复制</span>
              </>
            ) : (
              <>
                <Copy className="w-4 h-4" />
                <span className="text-sm">复制内容</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-6">
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 overflow-auto max-h-96">
          <pre className="text-sm font-mono text-gray-800 whitespace-pre-wrap break-words">
            {extractedText}
          </pre>
        </div>

        <div className="mt-4 text-sm text-gray-600 bg-blue-50 border border-blue-200 rounded-lg p-4">
          <p className="font-medium text-blue-800 mb-2">📝 说明：</p>
          <ul className="list-disc list-inside space-y-1 text-blue-700">
            <li>上方显示从 PDF 中提取的原始文本内容</li>
            <li>AI 将基于此内容进行解析和处理</li>
            <li>您可以复制内容进行查看或编辑</li>
            <li>确认内容无误后，处理将自动继续</li>
          </ul>
        </div>

        {/* Character count */}
        <div className="mt-4 text-right text-xs text-gray-500">
          字符数: {extractedText.length.toLocaleString()}
        </div>
      </div>
    </div>
  );
};
